import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { PermissionKey } from '../constants/permissions';
import { getNavigationLink, isRestaurantOperatingContext } from '../constants/management';
import { useOperatingContext } from '../utils/operatingContextLifecycle';
import OperatingContextGate from './OperatingContextGate';
import { normalizeRoleNames } from '../utils/roleNames';
import OperatingContextUnavailablePage from './OperatingContextUnavailablePage';

interface Props {
  children: React.ReactNode;
  roles?: string[];
  permission?: PermissionKey;
  contextIndependent?: boolean;
}

const ProtectedRoute: React.FC<Props> = ({ children, roles, permission, contextIndependent = false }) => {
  const location = useLocation();
  const { token, roles: userRoles, permissions } = useAppSelector((s) => s.auth);
  const context = useOperatingContext();
  const normalizedUserRoles = React.useMemo(() => normalizeRoleNames(userRoles), [userRoles]);
  const tokenPermissions = React.useMemo(() => {
    if (!token) return [] as string[];
    try {
      const payload = token.split('.')[1] || '';
      const decodedJson = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const decoded = JSON.parse(decodedJson) as { perm?: string | string[] };
      if (Array.isArray(decoded.perm)) return decoded.perm;
      if (typeof decoded.perm === 'string') return [decoded.perm];
    } catch {
      return [];
    }
    return [];
  }, [token]);
  const effectivePermissions = React.useMemo(() => Array.from(new Set([...permissions, ...tokenPermissions])), [permissions, tokenPermissions]);
  const hasRoleAccess = roles ? roles.some((r) => normalizedUserRoles.includes(r)) : false;
  const isAuthorized = permission
    ? effectivePermissions.includes(permission)
    : !roles || hasRoleAccess;

  if (!token) return <Navigate to="/login" replace />;
  if (!isAuthorized) return <Navigate to="/unauthorized" replace />;
  const contextualLink = getNavigationLink(location.pathname, location.search);
  if (contextIndependent) return <React.Fragment key={context.generation}>{children}</React.Fragment>;
  return <OperatingContextGate>
    {isRestaurantOperatingContext(context.groupId) && location.pathname === '/admin/audit-logs' &&
      !context.restaurantAuditReadsEnabled
      ? <p role="status">Журнал аудиту ресторану ще не активовано.</p>
      : isRestaurantOperatingContext(context.groupId) && contextualLink?.contextSupport === 'legacy'
      ? <OperatingContextUnavailablePage replacementPath={contextualLink.replacementPath} />
      : children}
  </OperatingContextGate>;
};
export default ProtectedRoute;
